import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { InstallPrompt, OfflineIndicator } from "@/components/PWAComponents";
import { adminAPI } from "@/lib/api";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Garage from "@/pages/Garage";
import VehicleForm from "@/pages/VehicleForm";
import VehicleDashboard from "@/pages/VehicleDashboard";
import MaintenanceList from "@/pages/MaintenanceList";
import ServiceForm from "@/pages/ServiceForm";
import BuildSheet from "@/pages/BuildSheet";
import ModForm from "@/pages/ModForm";
import DataBackup from "@/pages/DataBackup";
import MaintenanceTemplates from "@/pages/MaintenanceTemplates";
import CustomTemplates from "@/pages/CustomTemplates";
import Analytics from "@/pages/Analytics";
import PhotoGallery from "@/pages/PhotoGallery";
import Reminders from "@/pages/Reminders";
import PublicBuildSheet from "@/pages/PublicBuildSheet";
import PartsInventory from "@/pages/PartsInventory";
import Teams from "@/pages/Teams";
import TeamDetail from "@/pages/TeamDetail";
import ShopFinder from "@/pages/ShopFinder";
import Subscription from "@/pages/Subscription";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import PayPalSuccess from "@/pages/PayPalSuccess";
import Wishlist from "@/pages/Wishlist";
import CustomCategories from "@/pages/CustomCategories";
import RigComparison from "@/pages/RigComparison";
import TrailLogs from "@/pages/TrailLogs";
import MaintenanceForecast from "@/pages/MaintenanceForecast";
import TripPlanner from "@/pages/TripPlanner";
import CommunityTrails from "@/pages/CommunityTrails";
import AdminDashboard from "@/pages/AdminDashboard";
import AIAssistant from "@/pages/AIAssistant";
import KnowledgeBase from "@/pages/KnowledgeBase";

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute
      })
      .catch(() => {});
  });
}

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      adminAPI.logPageView(location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <BrowserRouter>
        <PageViewTracker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Garage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/backup"
            element={
              <ProtectedRoute>
                <DataBackup />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/new"
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/edit"
            element={
              <ProtectedRoute>
                <VehicleForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id"
            element={
              <ProtectedRoute>
                <VehicleDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/templates"
            element={
              <ProtectedRoute>
                <MaintenanceTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/maintenance"
            element={
              <ProtectedRoute>
                <MaintenanceList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/services/new"
            element={
              <ProtectedRoute>
                <ServiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:vehicleId/services/:serviceId/edit"
            element={
              <ProtectedRoute>
                <ServiceForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/build"
            element={
              <ProtectedRoute>
                <BuildSheet />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/mods/new"
            element={
              <ProtectedRoute>
                <ModForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:vehicleId/mods/:modId/edit"
            element={
              <ProtectedRoute>
                <ModForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <CustomTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <Analytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/gallery"
            element={
              <ProtectedRoute>
                <PhotoGallery />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reminders"
            element={
              <ProtectedRoute>
                <Reminders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/parts"
            element={
              <ProtectedRoute>
                <PartsInventory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams"
            element={
              <ProtectedRoute>
                <Teams />
              </ProtectedRoute>
            }
          />
          <Route
            path="/teams/:id"
            element={
              <ProtectedRoute>
                <TeamDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shops"
            element={
              <ProtectedRoute>
                <ShopFinder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/paypal/success"
            element={
              <ProtectedRoute>
                <PayPalSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wishlist"
            element={
              <ProtectedRoute>
                <Wishlist />
              </ProtectedRoute>
            }
          />
          <Route path="/shared/:shareToken" element={<PublicBuildSheet />} />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <CustomCategories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compare"
            element={
              <ProtectedRoute>
                <RigComparison />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vehicles/:id/trail-logs"
            element={
              <ProtectedRoute>
                <TrailLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/forecast"
            element={
              <ProtectedRoute>
                <MaintenanceForecast />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trips"
            element={
              <ProtectedRoute>
                <TripPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/community-trails"
            element={
              <ProtectedRoute>
                <CommunityTrails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ai"
            element={
              <ProtectedRoute>
                <AIAssistant />
              </ProtectedRoute>
            }
          />
          <Route
            path="/knowledge-base"
            element={
              <ProtectedRoute>
                <KnowledgeBase />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <InstallPrompt />
      <OfflineIndicator />
    </div>
  );
}

export default App;
